import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { toggleAriaExpanded } from './utils';
import { createMap } from './mapbox';
import mapboxgl from 'mapbox-gl';

// GSAP setup
gsap.registerPlugin(ScrollToPlugin);

// Elements
const canvas = document.querySelector('.map-canvas');
const routeContainer = document.querySelector('.map-routes');
const filter = document.querySelector('.map-filter');
const filterBox = filter?.querySelector('.map-filter-box');
const filterToggle = document.querySelector('.map-filter-toggle');
const filterToggleLabel = filterToggle?.querySelector('.map-filter-toggle-label');
const filterToggleLabelWhenOpen = filterToggleLabel?.dataset.labelWhenOpen;
const filterToggleLabelWhenClosed = filterToggleLabel?.textContent;
const filterGroups = filter?.querySelectorAll('.map-filter-group') ?? [];

// Data container variables
let map = null;
let markers = [];
let currentMarker = null;
let currentCard = null;
let bounds = null;
let filterIsOpen = false;
let filterHasChanged = false;
let currentCategories = [];
let clusterFeatures = [];
let clusteringIsReady = false;

const CLUSTER_SOURCE_ID = 'map-large-points';
const CLUSTER_LAYER_ID = 'map-large-clusters';
const CLUSTER_COUNT_LAYER_ID = 'map-large-cluster-count';
const CLUSTER_VISIBILITY_ZOOM = 11;

function getBoundsOptions(extraPaddingBottom = false) {
    if (window.matchMedia('(max-width: 767px)').matches) {
        return {
            padding: { top: 100, right: 30, bottom: (extraPaddingBottom ? 300 : 120), left: 30 }
        }
    } else {
        return {
            padding: { top: 100, right: 100, bottom: 100, left: 424 }
        };
    }
}

function setMarkerElementVisibility(markerElement, show) {
    markerElement.style.display = show ? '' : 'none';
}

function getCurrentZoom() {
    return map?.getZoom() ?? CLUSTER_VISIBILITY_ZOOM;
}

function isClusterZoom() {
    return getCurrentZoom() < CLUSTER_VISIBILITY_ZOOM;
}

function toFeatureCollection(features) {
    return {
        type: 'FeatureCollection',
        features
    };
}

function updateMarkerVisibility(showMarkers) {
    markers.forEach(marker => {
        setMarkerElementVisibility(marker.getElement(), showMarkers);
    });
}

function shouldHideMarkersBeforeClusteringIsReady() {
    return !clusteringIsReady && isClusterZoom();
}

function updateClusterVisibility() {
    if (!clusteringIsReady) {
        return;
    }

    const showClusters = isClusterZoom();
    const clustersVisibility = showClusters ? 'visible' : 'none';

    map.setLayoutProperty(CLUSTER_LAYER_ID, 'visibility', clustersVisibility);
    map.setLayoutProperty(CLUSTER_COUNT_LAYER_ID, 'visibility', clustersVisibility);
    updateMarkerVisibility(!showClusters);
}

function updateClusterData(features = clusterFeatures) {
    clusterFeatures = features;

    if (!clusteringIsReady) {
        return;
    }

    map.getSource(CLUSTER_SOURCE_ID)?.setData(toFeatureCollection(clusterFeatures));
    updateClusterVisibility();
}

function createClusterLayers() {
    map.addLayer({
        id: CLUSTER_LAYER_ID,
        type: 'circle',
        source: CLUSTER_SOURCE_ID,
        filter: ['has', 'point_count'],
        paint: {
            'circle-color': '#002342',
            'circle-radius': [
                'step',
                ['get', 'point_count'],
                18,
                10,
                24,
                50,
                30
            ],
            'circle-stroke-width': 3,
            'circle-stroke-color': '#FFFFFF'
        }
    });

    map.addLayer({
        id: CLUSTER_COUNT_LAYER_ID,
        type: 'symbol',
        source: CLUSTER_SOURCE_ID,
        filter: ['has', 'point_count'],
        layout: {
            'text-field': ['get', 'point_count_abbreviated'],
            'text-size': 13,
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold']
        },
        paint: {
            'text-color': '#FFFFFF'
        }
    });
}

function registerClusterEventListeners() {
    map.on('click', CLUSTER_LAYER_ID, event => {
        const clusterFeature = event.features?.[0];
        if (!clusterFeature) {
            return;
        }

        map.getSource(CLUSTER_SOURCE_ID).getClusterExpansionZoom(clusterFeature.properties.cluster_id, (error, zoom) => {
            if (error) {
                return;
            }

            map.easeTo({
                center: clusterFeature.geometry.coordinates,
                zoom
            });
        });
    });

    map.on('mouseenter', CLUSTER_LAYER_ID, () => {
        map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', CLUSTER_LAYER_ID, () => {
        map.getCanvas().style.cursor = '';
    });

    map.on('zoom', updateClusterVisibility);
}

function setupClustering() {
    if (map.getSource(CLUSTER_SOURCE_ID)) {
        clusteringIsReady = true;
        updateClusterData();
        return;
    }

    map.addSource(CLUSTER_SOURCE_ID, {
        type: 'geojson',
        data: toFeatureCollection(clusterFeatures),
        cluster: true,
        clusterMaxZoom: CLUSTER_VISIBILITY_ZOOM - 1,
        clusterRadius: 60
    });

    createClusterLayers();
    registerClusterEventListeners();

    clusteringIsReady = true;
    updateClusterData();
}

function createRouteFeature(route, lng, lat) {
    return {
        type: 'Feature',
        geometry: {
            type: 'Point',
            coordinates: [lng, lat]
        },
        properties: {
            kind: 'route',
            routeId: route.dataset.id
        }
    };
}

function createLandmarkFeature(lng, lat) {
    return {
        type: 'Feature',
        geometry: {
            type: 'Point',
            coordinates: [lng, lat]
        },
        properties: {
            kind: 'landmark'
        }
    };
}

function createRouteMarkerElement(route, addCategoryIdsToMarkers) {
    const markerElement = document.createElement('div');
    markerElement.className = 'map-marker';
    markerElement.dataset.id = route.dataset.id;
    markerElement.addEventListener('click', handleMarkerClick);

    if (shouldHideMarkersBeforeClusteringIsReady()) {
        setMarkerElementVisibility(markerElement, false);
    }

    if (addCategoryIdsToMarkers) {
        markerElement.dataset.categoryIds = route.dataset.categoryIds;
    } else {
        markerElement.classList.add('highlight');
    }

    return markerElement;
}

function createLandmarkMarkerElement(location) {
    const markerElement = document.createElement('a');
    markerElement.className = 'map-marker map-landmark';
    markerElement.dataset.label = location.dataset.label;
    markerElement.href = location.dataset.url;

    if (shouldHideMarkersBeforeClusteringIsReady()) {
        setMarkerElementVisibility(markerElement, false);
    }

    const label = document.createElement('span');
    label.className = 'map-landmark-label';
    label.textContent = location.dataset.label;
    markerElement.appendChild(label);

    return markerElement;
}

function createMarker(markerElement, lng, lat) {
    const marker = new mapboxgl.Marker(markerElement).setLngLat([lng, lat]).addTo(map);
    markers.push(marker);
    bounds.extend([lng, lat]);
    return marker;
}

function createLandmarks(map, markers, bounds, features) {
    const locations = document.querySelectorAll('.map-location');

    if (!locations.length) {
        return;
    }

    locations.forEach(location => {
        const { lat, lng } = location.dataset;
        const latitude = parseFloat(lat);
        const longitude = parseFloat(lng);

        createMarker(createLandmarkMarkerElement(location), longitude, latitude);
        features.push(createLandmarkFeature(longitude, latitude));
    });
}

function createMarkers(addCategoryIdsToMarkers = false) {
    const routes = routeContainer.querySelectorAll('.map-route');
    const features = [];

    if (!routes.length) {
        updateClusterData(features);
        return;
    }

    bounds = new mapboxgl.LngLatBounds();
    
    routes.forEach(route => {
        const lng = parseFloat(route.dataset.lng);
        const lat = parseFloat(route.dataset.lat);

        createMarker(createRouteMarkerElement(route, addCategoryIdsToMarkers), lng, lat);
        features.push(createRouteFeature(route, lng, lat));
    });
    
    createLandmarks(map, markers, bounds, features);
    updateClusterData(features);

    // if (routes.length > 1) {
        map.fitBounds(bounds, getBoundsOptions());
    // } else {
    //     map.flyTo({ center: bounds.getCenter(), zoom: 14 });
    // }
}

function removeMarkers() {
    markers.forEach(marker => marker.remove());
    markers = [];
}

function activateRoute(routeId) {
    const targetMarker = markers.find(marker => marker.getElement().dataset.id == routeId);
    const targetCard = routeContainer.querySelector('.map-route[data-id="' + routeId + '"]');

    if (!targetMarker || !targetCard) {
        return;
    }

    currentMarker?.getElement().classList.remove('active');
    currentCard?.classList.remove('active');

    targetMarker.getElement().classList.add('active');
    targetCard.classList.add('active');

    currentMarker = targetMarker;
    currentCard = targetCard;

    // Side effect 1: highlight the target marker and center the map on it
    if (window.matchMedia('(min-width: 767px)').matches) {
        map.flyTo({ center: targetMarker.getLngLat(), zoom: 12 });
    }

    // Side effect 2: scroll the route container to the target card
    const cardContainerHeight = routeContainer.getBoundingClientRect().height;
    const cardHeight = targetCard.getBoundingClientRect().height;
    const maxScroll = routeContainer.scrollHeight - cardContainerHeight;

    let targetPosition = targetCard.offsetTop - (cardContainerHeight / 2) + (cardHeight / 2);

    if (targetPosition < 0) {
        targetPosition = 0;
    } else if (targetPosition > maxScroll) {
        targetPosition = maxScroll;
    }

    gsap.to(routeContainer, { duration: 0.8, scrollTo: targetPosition, ease: 'power3.inOut' });

    // Side effect 3: close the filter
    closeFilter();
}

function deactivateRoute() {
    currentMarker?.getElement().classList.remove('active');
    currentCard?.classList.remove('active');

    currentMarker = currentCard = null;

    if (markers.length > 1) {
        map.fitBounds(bounds, getBoundsOptions());
    }
}

function handleMarkerClick(e) {
    const marker = e.target;
    
    if (currentMarker?.getElement() === marker) {
        deactivateRoute();
    } else {
        activateRoute(marker.dataset.id);
    }
}

function handleRouteContainerClick(e) {
    const isCloseButton = e.target.classList.contains('card-close') || e.target.closest('.card-close');
    const isShowButton = e.target.classList.contains('card-show-on-map') || e.target.closest('.card-show-on-map');
    const card = e.target.closest('.map-route');

    if (!isCloseButton && !isShowButton) {
        return;
    }

    e.stopPropagation();
    e.preventDefault();

    if (isShowButton && card !== currentCard) {
        activateRoute(card.dataset.id);
    } else if (isCloseButton || (isShowButton && card === currentCard)) {
        deactivateRoute();
    }
}

function handleFilterChange(e) {
    const checkbox = e.target;
    const group = checkbox.closest('.map-filter-group');
    if (!group) {
        return;
    }

    const parentCheckbox = group.querySelector('.map-filter-group-input');
    const childCheckboxes = group.querySelectorAll('.map-filter-item-input');
    const isParentCheckbox = checkbox === parentCheckbox;
    const allChildrenAreChecked = Array.from(childCheckboxes).every(childCheckbox => childCheckbox.checked);
    const anyChildIsChecked = Array.from(childCheckboxes).some(childCheckbox => childCheckbox.checked);

    if (isParentCheckbox) {
        childCheckboxes.forEach(childCheckbox => {
            childCheckbox.checked = checkbox.checked;
        });
    } else {
        if (allChildrenAreChecked) {
            parentCheckbox.indeterminate = false;
            parentCheckbox.checked = true;
        } else if (anyChildIsChecked) {
            parentCheckbox.indeterminate = true;
            parentCheckbox.checked = false;
        } else {
            parentCheckbox.indeterminate = false;
            parentCheckbox.checked = false;
        }
    }

    currentCategories = [];

    filter.querySelectorAll('.map-filter-item-input:checked').forEach(checkbox => {
        currentCategories.push(checkbox.value);
    });

    filterHasChanged = true;
}

function executeFilter() {
    if (!filterHasChanged) {
        return;
    }

    fetch(urls.ajax, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: 'action=filter_routes&categories=' + currentCategories.join('|')
    }).then(response => response.text()).then(result => {
        routeContainer.innerHTML = result;

        removeMarkers();
        createMarkers(!!currentCategories.length);

        filterHasChanged = false;
    });
}

function closeFilter() {
    if (filterIsOpen) {
        filterToggleLabel.textContent = filterToggleLabelWhenClosed;

        // It would be better to create a timeline outside of this function and reuse it, 
        // but that causes problems with the height animation because the inner panels animate too.
        gsap.timeline()
            .to(filterBox, { duration: 0.5, height: 0, y: -8, opacity: 0, ease: 'power3.inOut' })
            .set(filterBox, { display: 'none' });

        toggleAriaExpanded(filterToggle);
    }

    filterIsOpen = false;
}

function openFilter() {
    if (!filterIsOpen) {
        filterToggleLabel.textContent = filterToggleLabelWhenOpen;

        gsap.timeline()
            .set(filterBox, { display: 'block', y: -8, opacity: 0 })
            .to(filterBox, { duration: 0.5, height: 'auto', y: 0, opacity: 1, ease: 'power3.inOut' });

        toggleAriaExpanded(filterToggle);
    }

    filterIsOpen = true;
}

function handleFilterToggleClick() {
    if (filterIsOpen) {
        executeFilter();
        closeFilter();
    } else {
        openFilter();
    }
}

// Create map and markers
map = createMap({
    container: canvas,
    center: [4.945, 52.639],
    zoom: 9
});

map.on('load', setupClustering);

createMarkers();

// Event listeners
routeContainer.addEventListener('click', handleRouteContainerClick);
filter?.addEventListener('change', handleFilterChange);
filterToggle?.addEventListener('click', handleFilterToggleClick);

// Handle filter group toggles
filterGroups.forEach(group => {
    const toggle = group.querySelector('.map-filter-group-toggle');
    const items = group.querySelector('.map-filter-items');
    
    const animation = gsap.timeline({ paused: true })
        .to(items, { duration: 0.5, height: 'auto', ease: 'power3.inOut' });
    
    toggle.addEventListener('click', () => {
        const isOpen = group.classList.contains('open');

        group.classList.toggle('open');

        if (isOpen) {
            animation.reverse();
        } else {
            animation.play();
        }
    });
});